import { Dispatch, SetStateAction, useState } from 'react';
import {
  Appointment,
  CreateAppointmentRequest,
  createOrRescheduleAppointment as createOrRescheduleAppointmentApi,
  RescheduleAppointmentRequest,
} from '../api';
import { AxiosError } from 'axios';
import { CONFLICTED_ERROR_CODE, RESCHEDULE_PARTIAL_FAILED_ERROR_CODE } from '../utils/enums';
import { datadogRum } from '@datadog/browser-rum';
import { ErrorSource, RawError } from '@datadog/browser-core';

export type UseCreateOrRescheduleAppointment = () => {
  createOrRescheduleAppointment: (
    appointmentRequest: CreateAppointmentRequest | RescheduleAppointmentRequest
  ) => Promise<Appointment | null>;
  setIsSelectedTimeslotBusy: Dispatch<SetStateAction<boolean>>;
  isSelectedTimeslotBusy: boolean;
  isRescheduleApptPartialFailed: boolean;
  isCreateOrRescheduleApptFailed: boolean;
  createOrRescheduleAppointmentError: Error | null;
};

export const useCreateOrRescheduleAppointment: UseCreateOrRescheduleAppointment = () => {
  const [isSelectedTimeslotBusy, setIsSelectedTimeslotBusy] =
    useState<ReturnType<UseCreateOrRescheduleAppointment>['isSelectedTimeslotBusy']>(false);
  const [isRescheduleApptPartialFailed, setIsRescheduleApptPartialFailed] =
    useState<ReturnType<UseCreateOrRescheduleAppointment>['isRescheduleApptPartialFailed']>(false);
  const [isCreateOrRescheduleApptFailed, setIsCreateOrRescheduleApptFailed] =
    useState<ReturnType<UseCreateOrRescheduleAppointment>['isCreateOrRescheduleApptFailed']>(false);
  const [createOrRescheduleAppointmentError, setCreateOrRescheduleAppointmentError] =
    useState<ReturnType<UseCreateOrRescheduleAppointment>['createOrRescheduleAppointmentError']>(null);
  const createOrRescheduleAppointment = async (
    appointmentRequest: CreateAppointmentRequest | RescheduleAppointmentRequest
  ): Promise<Appointment | null> => {
    setIsSelectedTimeslotBusy(false);
    setIsCreateOrRescheduleApptFailed(false);
    setIsRescheduleApptPartialFailed(false);
    setCreateOrRescheduleAppointmentError(null);

    try {
      return await createOrRescheduleAppointmentApi(appointmentRequest);
    } catch (error) {
      setCreateOrRescheduleAppointmentError(error as Error);
      const { response } = error as AxiosError;
      if (response && response.status === CONFLICTED_ERROR_CODE) {
        setIsSelectedTimeslotBusy(true);
      } else if ((error as Error)?.message === RESCHEDULE_PARTIAL_FAILED_ERROR_CODE.toString()) {
        setIsRescheduleApptPartialFailed(true);
      } else {
        setIsCreateOrRescheduleApptFailed(true);
        (error as unknown as RawError).source = ErrorSource.SOURCE;
        datadogRum.addError(error);
      }
      return null;
    }
  };

  return {
    createOrRescheduleAppointment,
    setIsSelectedTimeslotBusy,
    isSelectedTimeslotBusy,
    isRescheduleApptPartialFailed,
    isCreateOrRescheduleApptFailed,
    createOrRescheduleAppointmentError,
  };
};
